import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Alert, Box, Snackbar } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TelefonBaseEntry } from '@codefluegel/wowis-rest-typescript-client';
import { useEffect, useState } from 'react';
import ThemedButton from '../button/ThemedButton';
import { useKundenApi } from '../../ApiClient';
import useMedia from '../../hooks/useMedia';
import PasswordModal from './PasswordModal';

const ContactModal = observer(
  ({
    open,
    onClose,
    topUserId,
    contactToEdit,
    lfdNr,
    updateTextField,
  }: {
    open: boolean;
    onClose: () => void;
    topUserId: number;
    contactToEdit: TelefonBaseEntry | undefined;
    lfdNr: number | undefined;
    updateTextField: (contact: TelefonBaseEntry) => void;
  }): JSX.Element => {
    const [email, setEmail] = useState('');
    const [tel, setTel] = useState('');
    const kundenApi = useKundenApi();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [showPassowrodDialog, setShowPassowrodDialog] = useState(false);
    const isMobile = useMedia('md');

    useEffect(() => {
      if (!open) {
        return;
      }

      if (contactToEdit?.email) {
        setEmail(contactToEdit.email);
      } else {
        setEmail('');
      }
      if (contactToEdit?.telnr) {
        setTel(contactToEdit.telnr);
      } else {
        setTel('');
      }
    }, [contactToEdit, open]);

    const handleClose = () => {
      setEmail('');
      setTel('');
      onClose();
    };

    const saveEntry = async () => {
      setSnackbarMessage('Error');
      if (email !== '' || tel !== '') {
        try {
          const newContact: TelefonBaseEntry = {
            telId: contactToEdit?.telId,
            lfd_nr: lfdNr,
            email,
            telnr: tel,
          };
          if (contactToEdit) {
            await kundenApi.updateKundenTelefonDaten(topUserId, newContact);
            updateTextField(newContact);
          } else {
            const { data } = await kundenApi.addKundenTelefonDaten(
              topUserId,
              newContact,
            );
            updateTextField(data);
          }
          handleClose();
        } catch (error) {
          if (error instanceof Error && typeof error.message === 'string') {
            setSnackbarMessage(error.message);
          }
          setSnackbarOpen(true);
        }
      } else {
        handleClose();
      }
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle color="secondary">
          Kontakt {contactToEdit === undefined ? 'hinzufügen' : 'editieren'}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingX: isMobile ? 0 : 10,
            }}
          >
            <TextField
              sx={{
                marginTop: 2,
                marginBottom: 2,
                width: 'auto',
                input: { color: '#58585A' },
              }}
              id="email"
              label="EMAIL"
              type="email"
              variant="standard"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />

            <TextField
              sx={{
                marginTop: 2,
                marginBottom: 2,
                width: 'auto',
                input: { color: '#58585A' },
              }}
              id="tel"
              label="TELEFONNUMMER"
              type="text"
              inputProps={{ maxLength: 20 }}
              variant="standard"
              value={tel}
              onChange={e => setTel(e.target.value)}
            />

            <DialogActions
              sx={{
                justifyContent: 'space-around',
                marginTop: 4,
                marginBottom: 2,
              }}
            >
              <Button sx={{ width: 180 }} onClick={handleClose}>
                Abbrechen
              </Button>
              <Box width={180}>
                <ThemedButton
                  label="Speichern"
                  onClick={() => setShowPassowrodDialog(true)}
                />
              </Box>
            </DialogActions>
          </Box>
        </DialogContent>
        <PasswordModal
          open={showPassowrodDialog}
          onClose={() => setShowPassowrodDialog(false)}
          onPasswordPassed={saveEntry}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  },
);

export default ContactModal;
